import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowRight, FiDownload, FiLayout, FiCode, FiDatabase, FiSmartphone, FiMonitor, FiCloud } from 'react-icons/fi';
import api, { getAssetUrl } from '../utils/api';

// Components
import ProjectCard from '../components/projects/ProjectCard';
import TestimonialCard from '../components/testimonials/TestimonialCard';
import SkillBar from '../components/skills/SkillBar';
import BlogCard from '../components/blog/BlogCard';

import PageTransition from '../components/ui/PageTransition';
import { useGSAPAnimations } from '../hooks/useGSAPAnimations';
import genericAvatar from '../assets/images/avatar.svg';
import businessConsulting from '../assets/images/business-consulting.svg';
import sortingVisualizer from '../assets/images/sorting-visualizer.svg';
import blogPost1 from '../assets/images/blog-post-1.svg';
import blogPost2 from '../assets/images/blog-post-2.svg';
import portfolioProject from '../assets/images/portfolio-project.svg';

const CyberOrb = lazy(() => import('../components/three/CyberOrb'));

const CountUp = ({ target, suffix }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let n = 0;
        const interval = setInterval(() => { n++; setCount(n); if (n >= target) clearInterval(interval); }, 1400 / target);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);
  return <span ref={ref}>{count}{suffix}</span>;
};

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [blogCarouselPosts, setBlogCarouselPosts] = useState([]);
  const [statsData, setStatsData] = useState([
    { key: 'yearsExperience', value: 3, label: 'Years Experience', suffix: '+' },
    { key: 'projectsBuilt', value: 20, label: 'Projects Built', suffix: '+' },
    { key: 'currentSemester', value: 6, label: 'Semester', suffix: 'th' },
    { key: 'companiesWorked', value: 2, label: 'Companies', suffix: '+' },
  ]);

  const [resumeUrl, setResumeUrl] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projRes, configRes, blogRes] = await Promise.all([
          api.get('/projects'),
          api.get('/config'),
          api.get('/blogs')
        ]);

        // Filter featured projects if any
        const allProjects = projRes.data;
        const featured = allProjects.filter(p => p.featured).slice(0, 3);
        setFeaturedProjects(featured.length > 0 ? featured : allProjects.slice(0, 3));

        // Set blogs
        setBlogCarouselPosts(blogRes.data.slice(0, 2));

        // Find resume URL
        const resume = configRes.data.find(c => c.key === 'resumeUrl');
        if (resume) setResumeUrl(resume.value);

        // Update stats from config
        if (configRes.data.length > 0) {
          const updatedStats = statsData.map(s => {
            const configItem = configRes.data.find(c => c.key === s.key);
            return configItem ? { ...s, value: parseInt(configItem.value) } : s;
          });
          setStatsData(updatedStats);
        }
      } catch (err) {
        console.error('Error fetching home data:', err);
      }
    };
    fetchData();
  }, []);

  const testimonials = [
    {
      id: 1,
      name: 'Flash Dynamics Syndicate',
      position: 'Web Design Company',
      content: 'Prince has been an invaluable asset to our team, developing and maintaining responsive websites with modern frameworks like React. His collaboration skills and attention to detail have consistently delivered high-quality web solutions.',
      avatar: genericAvatar,
    },
  ];

  const skills = [
    { name: 'HTML/CSS', percentage: 90 },
    { name: 'JavaScript', percentage: 85 },
    { name: 'C/C++', percentage: 95 },
    { name: 'Python', percentage: 85 },
    { name: 'MySQL', percentage: 80 },
    { name: 'MongoDB', percentage: 75 },
    { name: 'Node.js', percentage: 70 },
    { name: 'React', percentage: 75 },
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const roles = ['Cyber Security Student', 'Application Developer', 'Co-founder of the HiGen Labs', 'Web Developer', 'Problem Solver'];
  const [roleIndex, setRoleIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = roles[roleIndex];
    const speed = isDeleting ? 45 : 95;
    const timeout = setTimeout(() => {
      if (!isDeleting && displayed === current) { setTimeout(() => setIsDeleting(true), 1600); return; }
      if (isDeleting && displayed === '') { setIsDeleting(false); setRoleIndex(p => (p + 1) % roles.length); return; }
      setDisplayed(p => isDeleting ? p.slice(0, -1) : current.slice(0, p.length + 1));
    }, speed);
    return () => clearTimeout(timeout);
  }, [displayed, isDeleting, roleIndex]);

  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: null });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;

    setFormStatus({ loading: true, success: false, error: null });
    try {
      console.log('Sending contact message to:', api.defaults.baseURL + '/contact');
      console.log('Form data:', { ...formData, subject: 'Portfolio Message' });
      const res = await api.post('/contact', { ...formData, subject: 'Portfolio Message' });
      console.log('Submission response:', res.data);
      setFormStatus({ loading: false, success: true, error: null });
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus(prev => ({ ...prev, success: false })), 5000);
    } catch (err) {
      setFormStatus({ loading: false, success: false, error: 'Failed to send message. Please try again.' });
    }
  };

  useGSAPAnimations();

  return (
    <PageTransition>
      <HomeContainer>
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <CyberOrbContainer>
            <Suspense fallback={null}>
              <CyberOrb />
            </Suspense>
          </CyberOrbContainer>
          <div className="container">
            <HeroContent>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <HeroHeading>
                  I'm Prince Yadav,<br />
                  <TypewriterLine>
                    {displayed}<BlinkCursor>|</BlinkCursor>
                  </TypewriterLine>
                </HeroHeading>
                <HeroSubheading>
                  Frontend / Backend Developer & UI/UX Designer crafting beautiful, functional, and user-centered digital experiences.
                </HeroSubheading>
                <HeroButtons>
                  <PrimaryButton to="/projects">
                    View My Work <FiArrowRight />
                  </PrimaryButton>
                  <SecondaryButton to="/contact">
                    Contact Me
                  </SecondaryButton>
                </HeroButtons>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <HeroImage>
                  <div className="gradient-bg"></div>
                  <img src="/developer-portrait.svg" alt="Developer Portrait" />
                </HeroImage>
              </motion.div>
            </HeroContent>
          </div>
          <HeroBackground />
        </HeroSection>

        {/* About Section */}
        <Section id="about">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                About Me
              </motion.h2>
            </SectionTitle>
            <AboutContent>
              <motion.div
                className="about-text"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <p>
                  I'm a passionate web & App developer and designer with over 3 years of experience creating modern, responsive websites and applications. I specialize in building exceptional digital experiences that are both functional and beautiful.
                </p>
                <p>
                  My approach combines clean code, thoughtful design, and performance optimization to deliver solutions that exceed client expectations. I'm constantly learning and exploring new technologies to stay at the forefront of web development.
                </p>
                <DownloadButton
                  href={getAssetUrl(resumeUrl) || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiDownload /> {resumeUrl ? 'Download Resume' : 'Resume Not Available'}
                </DownloadButton>
              </motion.div>
              <motion.div
                className="skills-container"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={staggerContainer}
              >
                {skills.map((skill, index) => (
                  <motion.div key={index} variants={fadeInUp}>
                    <SkillBar name={skill.name} percentage={skill.percentage} />
                  </motion.div>
                ))}
              </motion.div>
            </AboutContent>
            <StatsRow>
              {statsData.map((s, i) => (
                <StatCard key={i}>
                  <StatNumber><CountUp target={s.value} suffix={s.suffix} /></StatNumber>
                  <StatLabel>{s.label}</StatLabel>
                </StatCard>
              ))}
            </StatsRow>
          </div>
        </Section>

        {/* Projects Section */}
        <Section id="projects" className="bg-alt">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                Featured Projects
              </motion.h2>
            </SectionTitle>
            <ProjectsGrid>
              {featuredProjects.map((project) => (
                <motion.div
                  className="gsap-card"
                  key={project._id || project.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeInUp}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </ProjectsGrid>
            <ViewAllButtonContainer>
              <ViewAllButton to="/projects">
                View All Projects <FiArrowRight />
              </ViewAllButton>
            </ViewAllButtonContainer>
          </div>
        </Section>

        {/* Services Section */}
        <Section id="services">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeInUp}>
                Services Offered
              </motion.h2>
            </SectionTitle>
            <ServicesGrid>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ServiceCard>
                  <ServiceIcon><FiLayout /></ServiceIcon>
                  <h3>Web Design</h3>
                  <p>Creative and intuitive designs that engage users and reflect your brand identity.</p>
                </ServiceCard>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ServiceCard>
                  <ServiceIcon><FiCode /></ServiceIcon>
                  <h3>Frontend Development</h3>
                  <p>Responsive and interactive user interfaces built with modern frameworks and best practices.</p>
                </ServiceCard>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ServiceCard>
                  <ServiceIcon><FiDatabase /></ServiceIcon>
                  <h3>Backend Development</h3>
                  <p>Robust server-side solutions that power your applications with security and efficiency.</p>
                </ServiceCard>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ServiceCard>
                  <ServiceIcon><FiMonitor /></ServiceIcon>
                  <h3>Responsive Design</h3>
                  <p>Mobile-first approach ensuring your website looks great on all devices and screen sizes.</p>
                </ServiceCard>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ServiceCard>
                  <ServiceIcon><FiSmartphone /></ServiceIcon>
                  <h3>Mobile App Development</h3>
                  <p>Creating seamless native and cross-platform mobile experiences using modern development practices.</p>
                </ServiceCard>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ServiceCard>
                  <ServiceIcon><FiCloud /></ServiceIcon>
                  <h3>Production & Deployments</h3>
                  <p>Configuring CI/CD pipelines, scaling cloud infrastructure, and maintaining high availability systems.</p>
                </ServiceCard>
              </motion.div>
            </ServicesGrid>
          </div>
        </Section>

        {/* Testimonials Section */}
        <Section id="testimonials" className="bg-alt">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeInUp}>
                Client Testimonials
              </motion.h2>
            </SectionTitle>
            {/* Testimonials */}
            <TestimonialsContainer>
              {testimonials.map((testimonial) => (
                <motion.div
                  key={testimonial.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeInUp}
                >
                  <TestimonialCard testimonial={testimonial} />
                </motion.div>
              ))}
            </TestimonialsContainer>
          </div>
        </Section>

        {/* Blog Section */}
        <Section id="blog">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeInUp}>
                Latest Insights
              </motion.h2>
            </SectionTitle>
            <BlogGrid>
              {blogCarouselPosts.map((post) => (
                <motion.div
                  className="gsap-card"
                  key={post._id || post.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeInUp}
                >
                  <BlogCard post={post} />
                </motion.div>
              ))}
            </BlogGrid>
            <ViewAllButtonContainer>
              <ViewAllButton to="/blog">
                View All Posts <FiArrowRight />
              </ViewAllButton>
            </ViewAllButtonContainer>
          </div>
        </Section>

        {/* Contact Section */}
        <Section id="contact" className="bg-alt">
          <div className="container">
            <SectionTitle className="section-title">
              <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeInUp}>
                Get In Touch
              </motion.h2>
            </SectionTitle>
            <ContactContainer>
              <motion.div
                className="contact-info"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <h3>Let's work together</h3>
                <p>
                  I'm currently available for freelance work. If you have a project that you want to get started, think you need my help with something, or just want to say hello, then get in touch.
                </p>
                <ContactInfo>
                  <li>princeyadav3033@gmail.com</li>
                  <li>+91 (878) 704-3175</li>
                  <li>Delhi , India</li>
                </ContactInfo>
              </motion.div>
              <motion.div
                className="contact-form"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <ContactForm onSubmit={handleContactSubmit}>
                  <FormGroup>
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      rows="5"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </FormGroup>
                  <SubmitButton type="submit" disabled={formStatus.loading}>
                    {formStatus.loading ? 'Sending...' : 'Send Message'}
                  </SubmitButton>
                  {formStatus.success && <p style={{ color: '#10b981', marginTop: '1rem' }}>Message sent successfully!</p>}
                  {formStatus.error && <p style={{ color: '#ef4444', marginTop: '1rem' }}>{formStatus.error}</p>}
                </ContactForm>
              </motion.div>
            </ContactContainer>
          </div>
        </Section>
      </HomeContainer>
    </PageTransition>
  );
};


const HomeContainer = styled.div`
  width: 100%;
`;

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 6rem 0;
  overflow: hidden;
  
  .light & {
    background-color: transparent;
  }
  
  .dark & {
    background-color: transparent;
  }
`;

const HeroBackground = styled.div`
  display: none; /* Let the global app aurora shine through */
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroHeading = styled.h1`
  font-size: 4rem;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, var(--text-primary-light), var(--primary-light));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  .dark & {
    background: linear-gradient(135deg, var(--text-primary-dark), var(--primary-dark));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
    margin-bottom: 1.25rem;
  }
  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

const HeroSubheading = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  max-width: 500px;
  
  .light & {
    color: var(--text-secondary-light);
  }
  
  .dark & {
    color: var(--text-secondary-dark);
  }
  
  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    align-items: center;
    width: 100%;
    
    a {
      width: 100%;
      max-width: 300px;
      justify-content: center;
    }
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
  color: white;
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  .dark & {
    background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: transparent;
  border: 2px solid var(--primary-light);
  color: var(--primary-light);
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-light);
    color: white;
  }
  
  .dark & {
    border-color: var(--primary-dark);
    color: var(--primary-dark);
    
    &:hover {
      background-color: var(--primary-dark);
      color: var(--bg-primary-dark);
    }
  }
`;

const HeroImage = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin-left: auto;
  
  .gradient-bg {
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    background: linear-gradient(135deg, var(--primary-light) 0%, var(--secondary-light) 100%);
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    opacity: 0.2;
    z-index: -1;
    animation: morphGradient 10s infinite alternate ease-in-out;
    
    .dark & {
      background: linear-gradient(135deg, var(--primary-dark) 0%, var(--secondary-dark) 100%);
    }
  }
  
  img {
    width: 100%;
    height: auto;
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    object-fit: cover;
  }
  
  @keyframes morphGradient {
    0% {
      border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    }
    50% {
      border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%;
    }
    100% {
      border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    }
  }
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;
  z-index: 1;
  
  &.bg-alt {
    .light & {
      background-color: rgba(255, 255, 255, 0.03);
    }
    
    .dark & {
      background-color: rgba(0, 0, 0, 0.15);
    }
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    display: inline-block;
    position: relative;
    font-size: 2.5rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
      
      .dark & {
        background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
      }
    }
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  .about-text {
    p {
      margin-bottom: 1.5rem;
      
      .light & {
        color: var(--text-secondary-light);
      }
      
      .dark & {
        color: var(--text-secondary-dark);
      }
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: transparent;
  border: 2px solid var(--primary-light);
  color: var(--primary-light);
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background-color: var(--primary-light);
    color: white;
  }
  
  .dark & {
    border-color: var(--primary-dark);
    color: var(--primary-dark);
    
    &:hover {
      background-color: var(--primary-dark);
      color: var(--bg-primary-dark);
    }
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ViewAllButtonContainer = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const ViewAllButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: transparent;
  border: 2px solid var(--primary-light);
  color: var(--primary-light);
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-light);
    color: white;
    transform: translateY(-3px);
  }
  
  .dark & {
    border-color: var(--primary-dark);
    color: var(--primary-dark);
    
    &:hover {
      background-color: var(--primary-dark);
      color: var(--bg-primary-dark);
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  padding: 2.5rem 2rem;
  border-radius: var(--border-radius);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
  -webkit-backdrop-filter: blur(var(--glass-blur));
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: -100%; width: 50%; height: 100%;
    background: linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent);
    transform: skewX(-20deg);
    transition: 0.6s;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  .light &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
    border-color: rgba(255, 255, 255, 0.8);
  }
  
  &:hover::before {
    left: 200%;
  }
  
  h3 {
    margin: 1.5rem 0 1rem;
    font-size: 1.5rem;
  }
  
  p {
    .light & {
      color: var(--text-secondary-light);
    }
    
    .dark & {
      color: var(--text-secondary-dark);
    }
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  min-width: 80px;
  min-height: 80px;
  max-width: 80px;
  max-height: 80px;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  line-height: 1;
  overflow: hidden;
  background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
  color: white;
  font-size: 2rem;

`;

const TestimonialsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  .contact-info {
    h3 {
      font-size: 1.8rem;
      margin-bottom: 1.5rem;
    }
    
    p {
      margin-bottom: 2rem;
      
      .light & {
        color: var(--text-secondary-light);
      }
      
      .dark & {
        color: var(--text-secondary-dark);
      }
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    margin-bottom: 1rem;
    
    .light & {
      color: var(--text-secondary-light);
    }
    
    .dark & {
      color: var(--text-secondary-dark);
    }
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
  }
  
  input, textarea {
    padding: 0.75rem 1rem;
    border-radius: var(--border-radius);
    border: 1px solid rgba(0, 0, 0, 0.1);
    font-family: inherit;
    font-size: 1rem;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: var(--primary-light);
      box-shadow: 0 0 0 2px rgba(255, 46, 147, 0.2);
      background: rgba(255, 255, 255, 0.8);
    }
    
    .dark & {
      background-color: rgba(0, 0, 0, 0.2);
      border-color: rgba(255, 255, 255, 0.1);
      color: var(--text-primary-dark);
      backdrop-filter: blur(10px);
      
      &:focus {
        border-color: var(--primary-dark);
        box-shadow: 0 0 0 2px rgba(255, 0, 127, 0.3);
        background: rgba(0, 0, 0, 0.4);
      }
    }
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  .dark & {
    background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
  }
`;

const CyberOrbContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: transparent;
  opacity: 0.9;
`;

const TypewriterLine = styled.span`
  display: block;
  background: linear-gradient(135deg, #A78BFA, #7C3AED, #2563EB);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  min-height: 1.3em;
`;

const BlinkCursor = styled.span`
  -webkit-text-fill-color: #A78BFA;
  animation: cursorBlink 1s step-end infinite;
  @keyframes cursorBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
  @media (max-width: 380px) { grid-template-columns: 1fr; }
`;

const StatCard = styled.div`
  padding: 1.5rem 1rem;
  border-radius: var(--border-radius);
  text-align: center;
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur));
  -webkit-backdrop-filter: blur(var(--glass-blur));
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow);
  @media (max-width: 768px) { padding: 1.25rem 0.75rem; }
`;

const StatNumber = styled.div`
  font-size: 2.4rem;
  font-weight: 700;
  background: linear-gradient(135deg, #7C3AED, #2563EB);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1;
  margin-bottom: 0.5rem;
  @media (max-width: 768px) { font-size: 1.8rem; }
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary-dark);
  .light & { color: var(--text-secondary-light); }
`;

export default Home;